import { useEffect, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import Masonry from 'react-masonry-css'

import Benefits from '@/components/Benefits'
import Icon from '@/components/Icon'
import { Slogan } from '@/components/Slogan'
import { getHistoryChannelList } from '@/network/api/channel'
import { Channel } from '@/network/api/channel/types'
import { useUserStore } from '@/store/user'

import {
  Card,
  Container,
  ContentTitle,
  EmptyState,
  Header,
  Logo,
  LogoutButton,
  MainContent,
  UserInfo,
  UserPanel,
  Wrapper,
} from './styles'

interface VisualCard {
  id: string
  imageUrl: string
  title: string
  height: number
  createdTime: string
  rawUrl: string
}

const Visuals = () => {
  const [cards, setCards] = useState<VisualCard[]>([])
  const [loading, setLoading] = useState(false)
  const [initialized, setInitialized] = useState(false) // 添加初始化状态
  const { userInfo, clearUser } = useUserStore()
  const { ref: loadMoreRef, inView } = useInView()
  const [page, setPage] = useState(1)
  const pageSize = 20
  const breakpointColumns = {
    default: 5, // 1800px以上显示5列
    1799: 4, // 1400-1799px显示4列
    1399: 3, // 1000-1399px显示3列
    999: 2, // 小于1000px显示2列
  }

  // 虚拟滚动设置
  const parentRef = useRef<HTMLDivElement>(null)

  const transformToCard = (channel: Channel) => {
    // 格式化时间戳为 Dec 18. 2024 格式
    const date = new Date(channel.createdAt)
    const formattedDate = date
      .toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
      .replace(',', '.')

    return {
      id: channel.channelId,
      imageUrl: channel?.ext?.screenUrl || '',
      title: channel?.ext?.rawUrl || '',
      height: channel?.ext?.picHeight,
      createdTime: formattedDate,
      rawUrl: channel?.ext?.rawUrl,
    }
  }

  const fetchMoreCards = async () => {
    const channels = await getHistoryChannelList({
      page,
      pageSize,
    })
    setPage((prev) => prev + 1)
    return channels.map(transformToCard)
  }

  // 加载更多数据
  const loadMore = async () => {
    if (loading) return
    setLoading(true)
    try {
      const newCards = await fetchMoreCards()
      setCards((prev) => [...prev, ...newCards])
    } finally {
      setLoading(false)
      setInitialized(true)
    }
  }

  useEffect(() => {
    if (inView) {
      loadMore()
    }
  }, [inView])

  const handleLogout = () => {
    clearUser()
  }

  const renderContent = () => {
    if (initialized && !cards.length) {
      return (
        <EmptyState>
          <div className="title">No items</div>
          <div className="subtitle">1. Pin the plugin to the top of the browser.</div>
          <div className="subtitle">2. The generated content will be displayed here.</div>
        </EmptyState>
      )
    }

    return (
      <Masonry breakpointCols={breakpointColumns} className="masonry-grid" columnClassName="masonry-grid_column">
        {cards
          .filter((item) => item.imageUrl)
          .map((card) => (
            <Card key={card.id} $height={card.height}>
              <a href={`/visual/${card.id}`} target="_blank" rel="noreferrer">
                <img src={card.imageUrl} alt={card.title} />
              </a>
              <a>{card.rawUrl}</a>
              <span>{card.createdTime}</span>
            </Card>
          ))}
      </Masonry>
    )
  }

  return (
    <Wrapper>
      <Container>
        <Header>
          <Logo>
            <Slogan />
          </Logo>
          <Benefits>
            <Icon type="lightning" />
            <span>{userInfo?.benefit?.credits || '--'}</span>
          </Benefits>
        </Header>
        {userInfo && <ContentTitle>{userInfo?.user.email.replace('@gmail.com', '')}&apos; s visuals</ContentTitle>}
        <MainContent ref={parentRef}>
          {renderContent()}
          <div ref={loadMoreRef}>{loading && <div>Loading...</div>}</div>
        </MainContent>

        <UserPanel>
          <LogoutButton onClick={handleLogout}>
            <Icon type="logout" />
          </LogoutButton>
          <UserInfo>
            {userInfo?.user?.picture ? (
              <img src={userInfo?.user?.picture} alt="avatar" />
            ) : (
              <span>{userInfo?.user?.email?.[0]}</span>
            )}
          </UserInfo>
        </UserPanel>
      </Container>
    </Wrapper>
  )
}

export default Visuals
